import React, { useState } from "react";
import { Button, PerexBox, Spacer, SystemMessage } from "@shoptet/ui";
import { Trans, useTranslation } from "react-i18next";
import {
  AdSystemStateT,
  AdSystemT,
  useAdSystemCampaignStateQuery,
  useChangeGoogleAdsSettingsStateMutation,
  useChangeMetaAdsSettingsStateMutation,
  useRequestConsultationMutation,
  useUpdateOrganizationMutation,
} from "../../graphql/generated/graphql";
import { isLessThanXHoursAgo } from "../../utils/isLessThanXHoursAgo";
import { Accordion } from "../Accordion";
import { Icon } from "../Icons";
import { ModalWrapper } from "../ModalWrapper";
import { SmallLoader } from "../SmallLoader";

interface DeactivateProps {
  adSystem?: AdSystemT;
}

export const Deactivate = ({ adSystem = AdSystemT.GoogleAdsT }: DeactivateProps) => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.Deactivate" });
  const [modalOpened, setModalOpened] = useState(false);
  const [updateInProgress, setUpdateInProgress] = useState(false);
  const { data, loading } = useAdSystemCampaignStateQuery({
    variables: {
      adSystem,
      dateTo: new Date().toISOString().split("T")[0],
    },
  });
  const [updateOrganizationMutation] = useUpdateOrganizationMutation();
  const [requestConsultationMutation, { loading: requestConsultationLoading }] = useRequestConsultationMutation();
  const [changeGoogleAdsSettingsMutation] = useChangeGoogleAdsSettingsStateMutation({
    onCompleted: () => {
      setUpdateInProgress(false);
    },
  });
  const [changeMetaAdsSettingsMutation] = useChangeMetaAdsSettingsStateMutation({
    onCompleted: () => {
      setUpdateInProgress(false);
    },
  });

  if (loading || !data?.organization) {
    return <SmallLoader />;
  }

  const adsSetting =
    adSystem === AdSystemT.GoogleAdsT ? data.organization.googleAdsSetting : data.organization.metaAdsSetting;
  const active = adsSetting?.state === AdSystemStateT.ActiveT;
  const paused = adsSetting?.state === AdSystemStateT.PausedT;

  if (!active && !paused) {
    return null;
  }

  const changeCampaignSetting = (state: AdSystemStateT) => {
    setUpdateInProgress(true);
    if (adSystem === AdSystemT.GoogleAdsT) {
      return changeGoogleAdsSettingsMutation({ variables: { input: { state } } });
    } else {
      return changeMetaAdsSettingsMutation({ variables: { input: { state } } });
    }
  };

  const requestConsultation = async () => {
    try {
      await requestConsultationMutation({ variables: { consultationRequestedAt: new Date() } });
    } catch (error) {
      alert(error);
    }
  };

  const consultationRequestedAt = data.organization.consultationRequestedAt;
  const consultationRequestedRecently = isLessThanXHoursAgo(consultationRequestedAt, 48);

  const campaignType = adSystem === AdSystemT.GoogleAdsT ? "Google" : "Meta";

  return (
    <Accordion header={active ? t("Headline active", { campaignType }) : t("Headline paused", { campaignType })}>
      {active ? (
        <>
          <SystemMessage description={t("Deactivate WarningBox")} level="alert" />
          {consultationRequestedRecently && (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "8px",
                padding: "16px",
                border: "2px solid var(--app-primary)",
                borderRadius: "var(--app-radius-box)",
                backgroundColor: "var(--app-surface)",
                margin: "32px 0 24px",
              }}
            >
              <Icon kind="info-item" style={{ color: "var(--app-primary)" }} />
              <div>
                <div>
                  {t("Your consultation request has been successfully submitted", { userEmail: data?.user?.email })}
                </div>
                <div style={{ margin: "0" }}>
                  <Trans
                    components={{ a: <a style={{ color: "var(--app-link)", fontWeight: "bold" }} target="blank" /> }}
                    i18nKey={"In the meantime, you can continue to manage your campaigns or see tips"}
                    t={t}
                  />
                </div>
              </div>
            </div>
          )}
          <Button
            data-test-id="request-consultation"
            disabled={requestConsultationLoading || consultationRequestedRecently}
            variant="primary"
            onClick={requestConsultation}
          >
            {t("I want to consult an expert")}
          </Button>
          <Spacer size="xl" />
          <Button
            data-test-id="deactivate"
            disabled={updateInProgress}
            variant="destructive"
            onClick={() => setModalOpened(true)}
          >
            {t("Deactivate button", { campaignType })}
          </Button>
        </>
      ) : (
        <>
          <PerexBox>{t("Reactivate InfoBox")}</PerexBox>
          <Button
            data-test-id="reactivate"
            disabled={updateInProgress}
            variant="action"
            onClick={() => changeCampaignSetting(AdSystemStateT.ActiveT)}
          >
            {t("Reactivate button", { campaignType })}
          </Button>
        </>
      )}
      <ModalWrapper
        isOpen={modalOpened}
        title={t("Deactivate Modal Headline", { campaignType })}
        onClose={() => setModalOpened(false)}
      >
        <div style={{ maxWidth: "400px" }}>
          <p>{t("Deactivate Modal Text", { campaignType })}</p>
        </div>
        <div className="btn-fullwidth-container" style={{ display: "flex", flexDirection: "row", marginTop: "20px" }}>
          <Button data-test-id="deactivate-cancel" variant="secondary" onClick={() => setModalOpened(false)}>
            {t("Deactivate Modal Cancel")}
          </Button>
          <div style={{ width: "100%", marginLeft: "10px" }}>
            <Button
              data-test-id="deactivate-proceed"
              disabled={updateInProgress}
              variant="destructive"
              onClick={() => {
                // Close the confirm modal before the mutations resolve, so it is
                // already gone when the questionnaire modal opens on top of it
                setModalOpened(false);
                changeCampaignSetting(AdSystemStateT.PausedT).then(() => {
                  updateOrganizationMutation({
                    variables: {
                      properties: {
                        stopCampaignQuestionnairePending: true,
                        stopCampaignQuestionnaireSource: campaignType.toLowerCase(),
                      },
                    },
                  });
                });
              }}
            >
              {t("Deactivate Modal Proceed")}
            </Button>
          </div>
        </div>
      </ModalWrapper>
    </Accordion>
  );
};
